
import { useEffect } from "react";
import { Habit, useHabits } from "@/context/SupabaseHabitContext";
import HabitGridHeader from "./HabitGridHeader";
import HabitGridBody from "./HabitGridBody";
import HabitGridEditNotice from "./HabitGridEditNotice";
import DiscardChangesDialog from "./DiscardChangesDialog";
import { useHabitGridState } from "./useHabitGridState";

interface HabitGridProps {
  habit: Habit;
  initialMonths?: number;
  externalEditMode?: boolean;
  onToggleEditMode?: () => void;
}

export default function HabitGrid({ 
  habit,
  initialMonths = 6, // Show 6 months by default
  externalEditMode,
  onToggleEditMode,
}: HabitGridProps) {
  const { habits } = useHabits();
  const {
    gridData,
    months,
    editMode: internalEditMode,
    confirmDiscard,
    totalContributions,
    isDateCompleted,
    setEditMode: setInternalEditMode,
    setConfirmDiscard,
    navigateMonths,
    handleToggleDay,
    handleSaveEdits,
    handleDiscardEdits
  } = useHabitGridState(habit, initialMonths);

  // Sync external edit mode with internal state
  useEffect(() => {
    if (externalEditMode !== undefined && externalEditMode !== internalEditMode) {
      setInternalEditMode(externalEditMode);
    }
  }, [externalEditMode, internalEditMode, setInternalEditMode]);

  // Use external edit mode if provided, otherwise use internal
  const editMode = externalEditMode !== undefined ? externalEditMode : internalEditMode;
  
  // Custom toggle function that updates both internal and external state
  const handleToggleEditMode = () => {
    if (onToggleEditMode) {
      onToggleEditMode();
    } else {
      setInternalEditMode(true);
    }
  };

  // Custom save handler that exits external edit mode
  const handleSave = () => {
    handleSaveEdits();
    if (onToggleEditMode && externalEditMode) {
      onToggleEditMode(); // Exit external edit mode
    }
  };

  // Custom cancel handler that exits external edit mode
  const handleCancel = () => {
    if (onToggleEditMode && externalEditMode) {
      onToggleEditMode(); // Exit external edit mode
    }
    setConfirmDiscard(true);
  };

  // Weekday labels
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className="habit-grid-container mt-4" data-testid="habit-grid">
      <HabitGridHeader
        habit={habit}
        totalContributions={totalContributions}
        editMode={editMode}
        onNavigate={navigateMonths}
        onToggleEditMode={handleToggleEditMode}
        onSave={handleSave}
        onCancel={handleCancel}
      />
      
      <HabitGridEditNotice visible={editMode} />
      
      <HabitGridBody 
        gridData={gridData}
        months={months}
        weekdays={weekdays}
        editMode={editMode}
        getIsCompleted={isDateCompleted}
        onToggleDay={handleToggleDay}
        cellColors={{
          empty: habit.color.empty,
          filled: habit.color.filled
        }}
        habit={habit}
        allHabits={habits}
      />

      <DiscardChangesDialog 
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        onDiscard={() => {
          handleDiscardEdits();
          if (onToggleEditMode && externalEditMode) {
            onToggleEditMode(); // Exit external edit mode
          }
        }}
      />
    </div>
  );
}
